import React, { useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { RoomCategory, Property } from 'api/types';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface RoomCategoryViewModalProps {
  isOpen: boolean;
  roomCategory: RoomCategory | null;
  onClose: () => void;
  onEdit?: (roomCategory: RoomCategory) => void;
  propertyLookup?: Record<string, Property>;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (categoryId: string) => void;
}

// ============================================================================
// READ-ONLY FIELD
// ============================================================================

const ReadOnlyField: React.FC<{ label: string; value?: string | number | boolean | null; span?: boolean }> = ({ label, value, span }) => (
  <div style={span ? { gridColumn: '1 / -1' } : undefined}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)' }}>
      {value != null && value !== '' ? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)) : '-'}
    </div>
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const RoomCategoryViewModal: React.FC<RoomCategoryViewModalProps> = ({
  isOpen,
  roomCategory,
  onClose,
  onEdit,
  propertyLookup,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !roomCategory) return;
    minimize({
      id: `${tabId}-view-modal-${roomCategory.id}`,
      title: `View - ${roomCategory.categoryCode}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(roomCategory.id),
    });
    onClose();
  }, [tabId, moduleName, roomCategory, minimize, onClose, onRestoreView]);

  if (!isOpen || !roomCategory) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">Room Category Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Property */}
          <div style={{ marginBottom: '1rem' }}>
            <ReadOnlyField
              label="Property"
              value={
                roomCategory.propertyId && propertyLookup?.[roomCategory.propertyId]
                  ? `${propertyLookup[roomCategory.propertyId].propertyCode} - ${propertyLookup[roomCategory.propertyId].propertyName}`
                  : roomCategory.propertyId || undefined
              }
              span
            />
          </div>

          {/* Basic Information */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <ReadOnlyField label="Category Code" value={roomCategory.categoryCode} />
            <ReadOnlyField label="Category Name" value={roomCategory.categoryName} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Short Description" value={roomCategory.shortDescription} />
            {roomCategory.longDescription && <ReadOnlyField label="Long Description" value={roomCategory.longDescription} />}
          </div>

          {/* Occupancy */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Base Occupancy" value={roomCategory.baseOccupancy} />
            <ReadOnlyField label="Max Occupancy" value={roomCategory.maxOccupancy} />
            <ReadOnlyField label="Max Adults" value={roomCategory.maxAdults} />
            <ReadOnlyField label="Max Children" value={roomCategory.maxChildren} />
          </div>

          {/* Bed & Room */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Bed Type" value={roomCategory.bedType} />
            <ReadOnlyField label="Bed Count" value={roomCategory.bedCount} />
            <ReadOnlyField label="Room Size (sqft)" value={roomCategory.roomSizeSqft} />
            <ReadOnlyField label="Room View" value={roomCategory.roomView} />
          </div>

          {/* Inventory & Display */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <ReadOnlyField label="Total Inventory" value={roomCategory.totalInventory} />
            <ReadOnlyField label="Display Order" value={roomCategory.displayOrder} />
            <ReadOnlyField label="Smoking Allowed" value={roomCategory.smokingAllowed} />
          </div>

          {/* Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1rem' }}>
            <div>
              <label className="form-label">Status</label>
              <div>
                <span className={`badge badge-${roomCategory.isActive ? 'active' : 'inactive'}`}>
                  {roomCategory.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => {
              onClose();
              onEdit(roomCategory);
            }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RoomCategoryViewModal;
